import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { getPosts } from "@/lib/api";
import { categoryStyle } from "@/lib/constants";
import { Phone, Globe, Building } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function MostRecentPage() {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        getPosts("recent").then(setPosts).catch(() => setPosts([]));
    }, []);

    return (
        <div data-testid="recent-page">
            <div className="px-4 md:px-8 lg:px-12 mt-10">
                <h1 className="section-title text-center mb-10">Most Recent</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 px-4 md:px-8 lg:px-12">
                <div className="lg:col-span-8 space-y-16">
                    {posts.map((post) => (
                        <article
                            key={post.id}
                            className="relative"
                            data-testid={`recent-article-${post.slug}`}
                        >
                            <span
                                className="badge-category absolute -top-2 left-0"
                                style={categoryStyle(post.category)}
                            >
                                {post.category}
                            </span>
                            <div className="pt-12">
                                <p className="font-sofia text-sm text-[#1B1B1B]/70">
                                    {formatDate(post.date)}
                                </p>
                                <h2 className="font-poppins font-bold uppercase tracking-tight text-2xl sm:text-3xl mt-2 leading-tight">
                                    <Link
                                        to={`/post/${post.slug}`}
                                        className="hover:text-[#E63B26] transition"
                                        data-testid={`recent-article-title-${post.slug}`}
                                    >
                                        {post.title}
                                    </Link>
                                </h2>
                                {post.sub_header && (
                                    <p className="mt-2 italic font-sofia text-lg text-[#1B1B1B]/80">
                                        {post.sub_header}
                                    </p>
                                )}

                                <div className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-6">
                                    <Link
                                        to={`/post/${post.slug}`}
                                        className="md:col-span-4 post-card block aspect-square overflow-hidden bg-[#1B1B1B] relative"
                                    >
                                        <img
                                            src={post.image_url}
                                            alt={post.title}
                                            className="post-card-image absolute inset-0 w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </Link>
                                    <div className="md:col-span-8 font-sofia space-y-3">
                                        <div className="text-sm text-[#1B1B1B]/85">
                                            <p className="font-bold text-[#1B1B1B]">
                                                {post.contact_name}
                                            </p>
                                            <p>
                                                <Building className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                                                {post.business}
                                            </p>
                                            <p>
                                                <Phone className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                                                {post.phone}
                                            </p>
                                            <p className="text-[#E63B26]">
                                                <Globe className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                                                {post.website}
                                            </p>
                                        </div>
                                        <p className="text-[#1B1B1B]/85 leading-relaxed whitespace-pre-line">
                                            {post.body}
                                        </p>
                                        <Link
                                            to={`/post/${post.slug}`}
                                            className="inline-block font-poppins text-sm tracking-[0.18em] uppercase text-[#E63B26] hover:text-[#1B1B1B] transition"
                                            data-testid={`recent-read-more-${post.slug}`}
                                        >
                                            … more
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="lg:col-span-4">
                    <Sidebar variant="with-ads" />
                </div>
            </div>
        </div>
    );
}
